import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import { AdminLangSwitch, useAdminLang } from './AdminLangContext';

const FONT = "'Helvetica Neue', Arial, sans-serif";
const SIDEBAR_WIDTH = 264;
const BREAKPOINT = 880;

const T = {
    fr: { logo: 'winston.ie', logoSub: 'gestion', backToSite: '← Retour au site', logout: 'Déconnexion', menu: 'Menu', close: 'Fermer' },
    en: { logo: 'winston.ie', logoSub: 'management', backToSite: '← Back to site', logout: 'Log out', menu: 'Menu', close: 'Close' },
};

// Sidebar de gestion — entièrement autonome (CSS injecté ici, aucune classe
// externe requise) pour ne plus jamais dépendre d'un design-system.css qui
// pourrait ne pas avoir été redéployé. Fixe sur desktop, tiroir plein écran
// derrière un bouton hamburger sous 880px.
function AdminNavbar({ sections = [], activeTab, onSelect }) {
    const navigate = useNavigate();
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const handleSelect = (id) => {
        onSelect(id);
        setOpen(false);
    };

    return (
        <>
            <style>{`
                .admin-nav-topbar {
                    display: none;
                }
                .admin-nav-sidebar {
                    position: fixed;
                    top: 0; left: 0; bottom: 0;
                    width: ${SIDEBAR_WIDTH}px;
                    background: #0e1013;
                    color: #f4efe4;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    overflow-y: auto;
                    z-index: 300;
                    box-sizing: border-box;
                    font-family: ${FONT};
                }
                .admin-nav-backdrop { display: none; }

                .admin-nav-logo {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 28px 24px 24px;
                    cursor: pointer;
                }
                .admin-nav-logo img { height: 30px; width: auto; display: block; flex-shrink: 0; }
                .admin-nav-logo-text { display: flex; flex-direction: column; line-height: 1.25; }
                .admin-nav-logo-text span:first-child { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
                .admin-nav-logo-text span:last-child { font-size: 10px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(244,239,228,0.42); }

                .admin-nav-close {
                    display: none;
                }

                .admin-nav-main { flex: 1; padding: 4px 14px 24px; }
                .admin-nav-section-label {
                    font-size: 10px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
                    color: rgba(244,239,228,0.34); margin: 22px 10px 8px;
                }
                .admin-nav-item {
                    display: block;
                    width: 100%;
                    text-align: left;
                    background: none;
                    border: none;
                    border-left: 2px solid transparent;
                    padding: 9px 10px;
                    margin: 1px 0;
                    font-family: ${FONT};
                    font-size: 13px;
                    font-weight: 500;
                    color: rgba(244,239,228,0.62);
                    cursor: pointer;
                    transition: color .18s ease, background .18s ease, border-color .18s ease;
                }
                .admin-nav-item:hover { color: #f4efe4; background: rgba(244,239,228,0.06); }
                .admin-nav-item[data-active="true"] {
                    color: #f4efe4;
                    font-weight: 700;
                    border-left-color: #f4efe4;
                    background: rgba(244,239,228,0.08);
                }

                .admin-nav-bottom {
                    padding: 18px 24px 26px;
                    border-top: 1px solid rgba(244,239,228,0.12);
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .admin-nav-bottom-link {
                    background: none; border: none; padding: 0; text-align: left; cursor: pointer;
                    font-family: ${FONT}; font-size: 12px; font-weight: 500;
                    color: rgba(244,239,228,0.55);
                }
                .admin-nav-bottom-link:hover { color: #f4efe4; }
                .admin-nav-logout {
                    background: none; border: none; padding: 0; text-align: left; cursor: pointer;
                    font-family: ${FONT}; font-size: 12px; font-weight: 700;
                    color: #d98f85;
                }
                .admin-nav-logout:hover { color: #f0a89e; }

                @media (max-width: ${BREAKPOINT}px) {
                    .admin-nav-topbar {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        position: fixed;
                        top: 0; left: 0; right: 0;
                        height: 60px;
                        background: #0e1013;
                        padding: 0 18px;
                        z-index: 250;
                        box-sizing: border-box;
                    }
                    .admin-nav-topbar img { height: 24px; width: auto; display: block; }
                    .admin-nav-hamburger {
                        background: none; border: none; padding: 8px; margin: -8px;
                        display: flex; flex-direction: column; gap: 4px; cursor: pointer;
                    }
                    .admin-nav-hamburger span { display: block; width: 20px; height: 1.5px; background: #f4efe4; }

                    .admin-nav-sidebar {
                        width: min(300px, 84vw);
                        transform: translateX(-100%);
                        transition: transform .3s cubic-bezier(.4,0,.2,1);
                    }
                    .admin-nav-sidebar.admin-nav-open { transform: translateX(0); }

                    .admin-nav-backdrop {
                        display: block;
                        position: fixed; inset: 0;
                        background: rgba(0,0,0,0.5);
                        z-index: 290;
                        opacity: 0;
                        pointer-events: none;
                        transition: opacity .3s ease;
                    }
                    .admin-nav-backdrop.admin-nav-open { opacity: 1; pointer-events: auto; }

                    .admin-nav-close {
                        display: block;
                        background: none; border: none; padding: 4px; cursor: pointer;
                        color: #f4efe4; font-size: 20px; line-height: 1;
                        font-family: ${FONT};
                    }
                }
            `}</style>

            {/* Barre mobile — visible sous 880px seulement */}
            <div className="admin-nav-topbar">
                <img src="/winston-smiley-white.svg" alt="winston.ie" onClick={() => navigate('/admin/manage')} style={{ cursor: 'pointer' }} />
                <button className="admin-nav-hamburger" aria-label={t.menu} onClick={() => setOpen(true)}>
                    <span /><span /><span />
                </button>
            </div>

            <div className={`admin-nav-backdrop ${open ? 'admin-nav-open' : ''}`} onClick={() => setOpen(false)} />

            <div className={`admin-nav-sidebar ${open ? 'admin-nav-open' : ''}`}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="admin-nav-logo" onClick={() => { navigate('/admin/manage'); setOpen(false); }}>
                            <img src="/winston-smiley-white.svg" alt="winston.ie" />
                            <div className="admin-nav-logo-text">
                                <span>{t.logo}</span>
                                <span>{t.logoSub}</span>
                            </div>
                        </div>
                        <button className="admin-nav-close" style={{ marginRight: '16px' }} aria-label={t.close} onClick={() => setOpen(false)}>✕</button>
                    </div>

                    <div className="admin-nav-main">
                        {sections.map(section => (
                            <div key={section.label}>
                                <p className="admin-nav-section-label">{section.label}</p>
                                {section.items.map(item => (
                                    <button
                                        key={item.id}
                                        className="admin-nav-item"
                                        data-active={activeTab === item.id}
                                        onClick={() => handleSelect(item.id)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="admin-nav-bottom">
                    <AdminLangSwitch />
                    <button className="admin-nav-bottom-link" onClick={() => navigate('/')}>{t.backToSite}</button>
                    <button className="admin-nav-logout" onClick={handleLogout}>{t.logout}</button>
                </div>
            </div>
        </>
    );
}

export { SIDEBAR_WIDTH, BREAKPOINT };
export default AdminNavbar;